import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function HeaderLogo() {
  return <Link to="/" className="flex items-center shrink-0">
      <img src="https://goldstar-executive.websiteshop.au/wp-content/uploads/2026/03/Logo-White.png" alt="Gold Star Executive Cleaning" className="h-[104px] w-[104px] object-contain" />
    </Link>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium text-white transition-colors hover:text-white/90 font-sans">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-full origin-center scale-x-0 bg-primary transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-1 p-4">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border/40 last:border-0" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <div className="mt-6">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90" asChild>
          <Link to="/contact-us">Contact Us</Link>
        </Button>
      </div>
    </nav>;
}
export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="site-header" className={`sticky top-0 z-50 w-full transition-all duration-300 dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-white/5 shadow-sm" : "bg-background border-b border-transparent"}`}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <HeaderLogo />
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center px-8 mx-auto">
            <MenuProvider menu_id="47" className="w-full flex justify-center">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden md:flex flex-shrink-0">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground bg-transparent transition-all duration-300 font-medium">
              <Link to="/contact-us">Contact Us</Link>
            </Button>
          </div>

          {}
          <div className="md:hidden flex items-center">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="text-foreground hover:text-primary hover:bg-transparent" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </header>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 md:hidden dark">
          {}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          
          {}
          <div className="absolute right-0 top-0 h-full w-[80%] max-w-xs bg-background border-l border-border shadow-xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-bold text-lg text-foreground">Menu</span>
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Close menu">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <MenuProvider menu_id="47">
                <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>
          </div>
        </div>}
    </>;
}